import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { Reveal, Overline } from "./Reveal";

const PRESS_SCANS = [
  { img: "/images/press-vogue-bab.jpg", label: "Vogue Accessory · 2014 & BAB — Adv" },
  { img: "/images/press-tustyle-1.jpg", label: "Tu Style · 2008" },
  { img: "/images/press-tustyle-2.jpg", label: "Tu Style · 2009" },
  { img: "/images/press-ornamenta.jpg", label: "Ornamenta · 2009" },
  { img: "/images/press-cosmopolitan.jpg", label: "Cosmopolitan · 2009" },
  { img: "/images/press-glamour.jpg", label: "Glamour · 2010" },
  { img: "/images/press-confidenze-full.jpg", label: "Confidenze" },
  { img: "/images/press-preziosa.jpg", label: "Preziosa Magazine · 2021" },
];

const Collaborations = () => {
  const { t } = useLanguage();
  const [zoom, setZoom] = useState(null);
  const openZoom = (i) => {
    setZoom(i);
    window.__lenis?.stop();
  };
  const closeZoom = useCallback(() => {
    setZoom(null);
    window.__lenis?.start();
  }, []);
  const prevZoom = useCallback(() => {
    setZoom((i) => (i - 1 + PRESS_SCANS.length) % PRESS_SCANS.length);
  }, []);
  const nextZoom = useCallback(() => {
    setZoom((i) => (i + 1) % PRESS_SCANS.length);
  }, []);

  useEffect(() => {
    if (zoom === null) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") closeZoom();
      if (e.key === "ArrowLeft") prevZoom();
      if (e.key === "ArrowRight") nextZoom();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [zoom, closeZoom, prevZoom, nextZoom]);

  return (
    <section id="collaborazioni" data-testid="collaborations-section" className="py-24 sm:py-36 bg-travertine-light">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <Reveal>
          <Overline>{t.collab.overline}</Overline>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-ink mt-4 max-w-3xl">
            {t.collab.title}
          </h2>
        </Reveal>

        <Reveal delay={0.1} className="mt-12">
          <div data-testid="press-grid" className="flex flex-wrap gap-x-10 gap-y-5">
            {t.collab.press.map((p) => (
              <span
                key={p}
                data-testid={`press-item-${p.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="font-serif italic text-2xl sm:text-3xl text-ink/60 hover:text-ink transition-colors duration-400 cursor-default"
              >
                {p}
              </span>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.15} className="mt-14">
          <div data-testid="press-scans-grid" className="columns-2 sm:columns-3 lg:columns-4 gap-5">
            {PRESS_SCANS.map((s, i) => (
              <figure
                key={s.label}
                data-testid={`press-scan-${s.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                onClick={() => openZoom(i)}
                className="group break-inside-avoid mb-5 frame-gold rounded-2xl overflow-hidden cursor-pointer transition-transform duration-500 hover:-translate-y-2"
              >
                <img
                  src={s.img}
                  alt={s.label}
                  loading="lazy"
                  className="w-full h-auto block"
                />
                <figcaption className="text-[10px] font-mono uppercase tracking-[0.2em] text-ink/50 py-3 text-center bg-travertine-light">
                  {s.label}
                </figcaption>
              </figure>
            ))}
          </div>
        </Reveal>
      </div>

      <AnimatePresence>
        {zoom !== null && (
          <motion.div
            data-testid="press-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[95] flex items-center justify-center p-4 sm:p-10 bg-ink/75 backdrop-blur-sm"
            onClick={closeZoom}
          >
            <motion.figure
              key={PRESS_SCANS[zoom].img}
              initial={{ opacity: 0, scale: 0.9, y: 24 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.92, y: 16 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="max-w-[92vw] max-h-[88vh]"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={PRESS_SCANS[zoom].img}
                alt={PRESS_SCANS[zoom].label}
                className="max-w-[92vw] max-h-[80vh] w-auto h-auto object-contain rounded-[24px] frame-gold mx-auto"
              />
              <figcaption
                data-testid="press-lightbox-caption"
                className="text-center text-[11px] font-mono uppercase tracking-[0.25em] text-travertine-light/80 mt-4"
              >
                {PRESS_SCANS[zoom].label}
              </figcaption>
            </motion.figure>
            <button
              data-testid="press-lightbox-prev"
              onClick={(e) => { e.stopPropagation(); prevZoom(); }}
              aria-label="Previous"
              className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center rounded-full bg-travertine-light/90 text-ink hover:bg-gold hover:text-travertine-light transition-colors duration-300"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              data-testid="press-lightbox-next"
              onClick={(e) => { e.stopPropagation(); nextZoom(); }}
              aria-label="Next"
              className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center rounded-full bg-travertine-light/90 text-ink hover:bg-gold hover:text-travertine-light transition-colors duration-300"
            >
              <ChevronRight size={20} />
            </button>
            <button
              data-testid="press-lightbox-close"
              onClick={closeZoom}
              aria-label="Close"
              className="absolute top-5 right-5 w-11 h-11 flex items-center justify-center rounded-full bg-travertine-light/90 text-ink hover:bg-gold hover:text-travertine-light transition-colors duration-300"
            >
              <X size={18} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Collaborations;
